/**
 * Client Detail → Timesheet Projects: gold-fact rows, FY/calendar filters,
 * consolidated stats, invoice period billed (USD), and grade-tech expenses.
 */

import type { InvoiceRecord } from './invoiceUtils'
import {
	invoiceInCalendarPeriodForFilter,
	invoiceInFYPeriodForFilter,
	invoiceInAnyCalendarSelection,
	invoiceInFYSelection,
	invoiceRowInvoicedPaidOutstanding,
} from './invoiceUtils'
import type { RateConfig } from './rateConfig'
import { getRateForPeriod } from './rateConfig'
import {
	getMaxIndianFYStartYear,
	getYearMonthsForFYQuarter,
	getYearMonthsForFullFY,
} from './indianFY'

/* ─── Types ─────────────────────────────────────────────────────────────── */

export interface ClientProjectMappingRow {
	project_id: number
	client_id?: number | string
	project_name?: string
	project_description?: string
	is_active?: number
	project_category?: string
	source?: string
	[key: string]: unknown
}

/** One row from gold_fact_project_user_monthly_project_id_*.json */
export interface GoldFactProjectUserMonthRow {
	year?: number
	month?: number
	user_id?: number
	full_name?: string
	username?: string
	total_hours?: number | string
	billable_hours?: number | string
	non_billable_hours?: number | string
	entries_count?: number | string
	resource_grade?: string
	resource_technology?: string
	[key: string]: unknown
}

export interface ConsolidatedStats {
	totalHours: number
	billableHours: number
	nonBillableHours: number
	utilizationRate: number
	totalUsers: number
	totalEntries: number
	totalMonths: number
	dateRange: { start: string | null; end: string | null }
	projectsLoaded: number
	projectStatsMap: Record<
		number,
		{
			billableHours: number
			totalHours: number
			nonBillableHours: number
			totalExpenses: number
			costOfNonBillable: number
		}
	>
	totalExpenses: number | null
	costOfNonBillable: number | null
}

export interface ClientRevenueTrendRow {
	contact_id?: number | string
	revenue_month?: string
	monthly_revenue?: number | string
	[key: string]: unknown
}

export interface ClientInvoiceByPeriodJson {
	by_year_month?: Record<
		string,
		{ clients?: Record<string, number | string> }
	>
	by_year?: Record<string, { clients?: Record<string, number | string> }>
	[key: string]: unknown
}

export const DEFAULT_EXPENSE_RATE = 20

/* ─── FY on work month (timesheet entry calendar) ───────────────────────── */

export function getFYFromWorkMonth(
	year: number | string | undefined,
	month: number | string | undefined,
): { fyLabel: string; quarter: string } {
	const m = parseInt(String(month), 10) || 0
	const y = parseInt(String(year), 10) || 0
	const fyStart = m >= 4 ? y : y - 1
	const fyLabel = fyStart ? `${fyStart}-${String(fyStart + 1).slice(-2)}` : ''
	let quarter = ''
	if (m >= 4 && m <= 6) quarter = 'q1'
	else if (m >= 7 && m <= 9) quarter = 'q2'
	else if (m >= 10 && m <= 12) quarter = 'q3'
	else if (m >= 1 && m <= 3) quarter = 'q4'
	return { fyLabel, quarter }
}

export function entryMatchesWorkPeriodFilters(
	entry: GoldFactProjectUserMonthRow,
	calYear: string,
	calMonth: string,
	fyLabel: string,
	fyQuarter: string,
): boolean {
	if (calYear && entry.year !== parseInt(calYear, 10)) return false
	if (calMonth && entry.month !== parseInt(calMonth, 10)) return false
	if (fyLabel || fyQuarter) {
		const fy = getFYFromWorkMonth(entry.year, entry.month)
		if (fyLabel && fy.fyLabel !== fyLabel) return false
		if (fyQuarter && fy.quarter !== fyQuarter) return false
	}
	return true
}

function workEntryMatchesCalSlices(
	ey: number,
	em: number,
	calYears: string[],
	calMonths: string[],
): boolean {
	const yOk =
		calYears.length === 0 ||
		calYears.some((cy) => parseInt(String(cy), 10) === ey)
	const mp = String(em).padStart(2, '0')
	const mOk =
		calMonths.length === 0 ||
		calMonths.some((cm) => {
			const n = parseInt(String(cm), 10)
			return String(n).padStart(2, '0') === mp
		})
	return yOk && mOk
}

function workEntryMatchesFYSlices(
	ey: number,
	em: number,
	fyStarts: string[],
	fyQuartersUpper: string[],
): boolean {
	const entryFyStart = em >= 4 ? ey : ey - 1
	if (!fyStarts.some((s) => parseInt(String(s), 10) === entryFyStart)) {
		return false
	}
	const qs = fyQuartersUpper
		.map((q) => String(q).trim().toLowerCase())
		.filter((q) => q === 'q1' || q === 'q2' || q === 'q3' || q === 'q4')
	if (qs.length === 0) return true
	const fy = getFYFromWorkMonth(ey, em)
	return qs.some((q) => q === fy.quarter)
}

/** Multi-select period: work-month calendar and/or India FY start years + quarters. */
export function entryMatchesWorkPeriodFiltersSlices(
	entry: GoldFactProjectUserMonthRow,
	calYears: string[],
	calMonths: string[],
	fyStarts: string[],
	fyQuarters: string[],
): boolean {
	const ey = entry.year != null ? Number(entry.year) : NaN
	const em = entry.month != null ? Number(entry.month) : NaN
	if (Number.isNaN(ey) || Number.isNaN(em)) return false

	const calOn = calYears.length > 0 || calMonths.length > 0
	const fyOn = fyStarts.length > 0
	if (!calOn && !fyOn) return true

	if (calOn && !workEntryMatchesCalSlices(ey, em, calYears, calMonths)) {
		return false
	}
	if (fyOn && !workEntryMatchesFYSlices(ey, em, fyStarts, fyQuarters)) {
		return false
	}
	return true
}

/* ─── Grade-tech expense rates ──────────────────────────────────────────── */

export function getRateForEntry(
	entry: GoldFactProjectUserMonthRow,
	gradeTechRateByGradeTech: Record<string, number>,
	defaultRate: number,
): number {
	const g =
		entry.resource_grade != null ? String(entry.resource_grade).trim() : ''
	const t =
		entry.resource_technology != null
			? String(entry.resource_technology).trim()
			: ''
	if (g && t && typeof gradeTechRateByGradeTech[`${g}|${t}`] === 'number') {
		return gradeTechRateByGradeTech[`${g}|${t}`]
	}
	return defaultRate
}

export function computeExpensesFromEntries(
	entries: GoldFactProjectUserMonthRow[],
	gradeTechRateByGradeTech: Record<string, number>,
	defaultRate: number,
): { totalExpenses: number | null; costOfNonBillable: number | null } {
	if (!Array.isArray(entries) || entries.length === 0) {
		return { totalExpenses: null, costOfNonBillable: null }
	}
	const def =
		defaultRate != null && !Number.isNaN(Number(defaultRate))
			? Number(defaultRate)
			: DEFAULT_EXPENSE_RATE
	let totalExpenses = 0
	let costOfNonBillable = 0
	entries.forEach((entry) => {
		const rate = getRateForEntry(entry, gradeTechRateByGradeTech, def)
		const bh = parseFloat(String(entry.billable_hours || 0)) || 0
		const nb = parseFloat(String(entry.non_billable_hours || 0)) || 0
		totalExpenses += bh * rate
		costOfNonBillable += nb * rate
	})
	return { totalExpenses, costOfNonBillable }
}

/* ─── Consolidated stats ────────────────────────────────────────────────── */

export function computeConsolidatedStatsFromCache(
	allProjectsData: { projectId: number; data: GoldFactProjectUserMonthRow[] }[],
	calYear: string,
	calMonth: string,
	fyLabel: string,
	fyQuarter: string,
	gradeTechRateByGradeTech: Record<string, number>,
	defaultRate: number,
): ConsolidatedStats {
	let totalHours = 0
	let billableHours = 0
	let nonBillableHours = 0
	let totalEntries = 0
	let totalExpenses = 0
	let costOfNonBillable = 0
	const uniqueUsers = new Set<number>()
	const uniqueMonths = new Set<string>()
	let dateStart: string | null = null
	let dateEnd: string | null = null
	const projectStatsMap: ConsolidatedStats['projectStatsMap'] = {}

	const useGradeTech =
		gradeTechRateByGradeTech &&
		typeof gradeTechRateByGradeTech === 'object' &&
		Object.keys(gradeTechRateByGradeTech).length > 0
	const defRate =
		defaultRate != null && !Number.isNaN(Number(defaultRate))
			? Number(defaultRate)
			: DEFAULT_EXPENSE_RATE

	allProjectsData.forEach(({ projectId, data }) => {
		if (!projectStatsMap[projectId]) {
			projectStatsMap[projectId] = {
				billableHours: 0,
				totalHours: 0,
				nonBillableHours: 0,
				totalExpenses: 0,
				costOfNonBillable: 0,
			}
		}
		if (!Array.isArray(data)) return
		data.forEach((entry) => {
			if (!entryMatchesWorkPeriodFilters(entry, calYear, calMonth, fyLabel, fyQuarter))
				return
			const th = parseFloat(String(entry.total_hours || 0)) || 0
			const bh = parseFloat(String(entry.billable_hours || 0)) || 0
			const nb = parseFloat(String(entry.non_billable_hours || 0)) || 0
			totalHours += th
			billableHours += bh
			nonBillableHours += nb
			projectStatsMap[projectId].totalHours += th
			projectStatsMap[projectId].billableHours += bh
			projectStatsMap[projectId].nonBillableHours += nb
			if (useGradeTech) {
				const rate = getRateForEntry(entry, gradeTechRateByGradeTech, defRate)
				const entryExpense = bh * rate
				const entryCostNb = nb * rate
				totalExpenses += entryExpense
				costOfNonBillable += entryCostNb
				projectStatsMap[projectId].totalExpenses += entryExpense
				projectStatsMap[projectId].costOfNonBillable += entryCostNb
			}
			totalEntries += parseInt(String(entry.entries_count || 0), 10) || 0
			if (entry.user_id != null) uniqueUsers.add(Number(entry.user_id))
			const y = entry.year
			const mo = entry.month
			if (y != null && mo != null) {
				const dateKey = `${y}-${String(mo).padStart(2, '0')}`
				uniqueMonths.add(dateKey)
				if (!dateStart || dateKey < dateStart) dateStart = dateKey
				if (!dateEnd || dateKey > dateEnd) dateEnd = dateKey
			}
		})
	})

	const utilizationRate =
		totalHours > 0 ? (billableHours / totalHours) * 100 : 0

	return {
		totalHours,
		billableHours,
		nonBillableHours,
		utilizationRate,
		totalUsers: uniqueUsers.size,
		totalEntries,
		totalMonths: uniqueMonths.size,
		dateRange: { start: dateStart, end: dateEnd },
		projectsLoaded: allProjectsData.length,
		projectStatsMap,
		totalExpenses: useGradeTech ? totalExpenses : null,
		costOfNonBillable: useGradeTech ? costOfNonBillable : null,
	}
}

export function computeConsolidatedStatsFromCacheSlices(
	allProjectsData: { projectId: number; data: GoldFactProjectUserMonthRow[] }[],
	calYears: string[],
	calMonths: string[],
	fyStarts: string[],
	fyQuarters: string[],
	gradeTechRateByGradeTech: Record<string, number>,
	defaultRate: number,
): ConsolidatedStats {
	let totalHours = 0
	let billableHours = 0
	let nonBillableHours = 0
	let totalEntries = 0
	let totalExpenses = 0
	let costOfNonBillable = 0
	const uniqueUsers = new Set<number>()
	const uniqueMonths = new Set<string>()
	let dateStart: string | null = null
	let dateEnd: string | null = null
	const projectStatsMap: ConsolidatedStats['projectStatsMap'] = {}

	const useGradeTech =
		gradeTechRateByGradeTech &&
		typeof gradeTechRateByGradeTech === 'object' &&
		Object.keys(gradeTechRateByGradeTech).length > 0
	const defRate =
		defaultRate != null && !Number.isNaN(Number(defaultRate))
			? Number(defaultRate)
			: DEFAULT_EXPENSE_RATE

	allProjectsData.forEach(({ projectId, data }) => {
		if (!projectStatsMap[projectId]) {
			projectStatsMap[projectId] = {
				billableHours: 0,
				totalHours: 0,
				nonBillableHours: 0,
				totalExpenses: 0,
				costOfNonBillable: 0,
			}
		}
		if (!Array.isArray(data)) return
		data.forEach((entry) => {
			if (
				!entryMatchesWorkPeriodFiltersSlices(
					entry,
					calYears,
					calMonths,
					fyStarts,
					fyQuarters,
				)
			) {
				return
			}
			const th = parseFloat(String(entry.total_hours || 0)) || 0
			const bh = parseFloat(String(entry.billable_hours || 0)) || 0
			const nb = parseFloat(String(entry.non_billable_hours || 0)) || 0
			totalHours += th
			billableHours += bh
			nonBillableHours += nb
			projectStatsMap[projectId].totalHours += th
			projectStatsMap[projectId].billableHours += bh
			projectStatsMap[projectId].nonBillableHours += nb
			if (useGradeTech) {
				const rate = getRateForEntry(entry, gradeTechRateByGradeTech, defRate)
				const entryExpense = bh * rate
				const entryCostNb = nb * rate
				totalExpenses += entryExpense
				costOfNonBillable += entryCostNb
				projectStatsMap[projectId].totalExpenses += entryExpense
				projectStatsMap[projectId].costOfNonBillable += entryCostNb
			}
			totalEntries += parseInt(String(entry.entries_count || 0), 10) || 0
			if (entry.user_id != null) uniqueUsers.add(Number(entry.user_id))
			const y = entry.year
			const mo = entry.month
			if (y != null && mo != null) {
				const dateKey = `${y}-${String(mo).padStart(2, '0')}`
				uniqueMonths.add(dateKey)
				if (!dateStart || dateKey < dateStart) dateStart = dateKey
				if (!dateEnd || dateKey > dateEnd) dateEnd = dateKey
			}
		})
	})

	const utilizationRate =
		totalHours > 0 ? (billableHours / totalHours) * 100 : 0

	return {
		totalHours,
		billableHours,
		nonBillableHours,
		utilizationRate,
		totalUsers: uniqueUsers.size,
		totalEntries,
		totalMonths: uniqueMonths.size,
		dateRange: { start: dateStart, end: dateEnd },
		projectsLoaded: allProjectsData.length,
		projectStatsMap,
		totalExpenses: useGradeTech ? totalExpenses : null,
		costOfNonBillable: useGradeTech ? costOfNonBillable : null,
	}
}

/* ─── Invoice period billed (USD) — billed month rules ─────────────────── */

function fyStartYearFromLabel(filterFYYear: string): number | null {
	if (!filterFYYear) return null
	const start = parseInt(String(filterFYYear).split('-')[0], 10)
	return Number.isNaN(start) ? null : start
}

/** Invoices matching the same period rules as timesheet filters (billed month / FY). */
export function filterInvoiceRecordsForTimesheetPeriod(
	records: InvoiceRecord[],
	filterYear: string,
	filterMonth: string,
	filterFYYear: string,
	filterFYQuarter: string,
): InvoiceRecord[] {
	if (!records.length) return []
	const hasFilter =
		!!filterYear ||
		!!filterMonth ||
		!!filterFYYear ||
		!!filterFYQuarter
	if (!hasFilter) return []

	const fyStart = filterFYYear ? fyStartYearFromLabel(filterFYYear) : null
	const quarterUpper = (filterFYQuarter || '').toUpperCase()
	const q =
		quarterUpper === 'Q1' ||
		quarterUpper === 'Q2' ||
		quarterUpper === 'Q3' ||
		quarterUpper === 'Q4'
			? quarterUpper
			: null

	const monthPadded =
		filterMonth != null && filterMonth !== ''
			? String(filterMonth).padStart(2, '0')
			: null

	return records.filter((r) => {
		if (filterFYYear && (filterFYQuarter || q)) {
			if (fyStart == null) return false
			return invoiceInFYPeriodForFilter(
				r,
				String(fyStart),
				q || quarterUpper || null,
			)
		}
		if (filterFYYear) {
			if (fyStart == null) return false
			return invoiceInFYPeriodForFilter(r, String(fyStart), null)
		}
		if (filterYear && filterMonth) {
			return invoiceInCalendarPeriodForFilter(r, filterYear, monthPadded)
		}
		if (filterYear) {
			return invoiceInCalendarPeriodForFilter(r, filterYear, null)
		}
		return false
	})
}

export function filterInvoiceRecordsForTimesheetPeriodSlices(
	records: InvoiceRecord[],
	calYears: string[],
	calMonths: string[],
	fyStarts: string[],
	fyQuarters: string[],
): InvoiceRecord[] {
	if (!records.length) return []
	const has =
		calYears.length > 0 ||
		calMonths.length > 0 ||
		fyStarts.length > 0 ||
		fyQuarters.length > 0
	if (!has) return []
	return records.filter((r) => {
		const calOn = calYears.length > 0 || calMonths.length > 0
		const fyOn = fyStarts.length > 0
		if (calOn && !invoiceInAnyCalendarSelection(r, calYears, calMonths)) {
			return false
		}
		if (fyOn && !invoiceInFYSelection(r, fyStarts, fyQuarters)) {
			return false
		}
		return true
	})
}

export function getPeriodTotalBilledUsdFromInvoiceRecords(
	records: InvoiceRecord[],
	filterYear: string,
	filterMonth: string,
	filterFYYear: string,
	filterFYQuarter: string,
): number | null {
	if (!records.length) return null
	const hasFilter =
		!!filterYear ||
		!!filterMonth ||
		!!filterFYYear ||
		!!filterFYQuarter
	if (!hasFilter) return null
	const filtered = filterInvoiceRecordsForTimesheetPeriod(
		records,
		filterYear,
		filterMonth,
		filterFYYear,
		filterFYQuarter,
	)
	let usd = 0
	filtered.forEach((inv) => {
		usd += parseFloat(String(inv.amount_usd)) || 0
	})
	return usd
}

/** Sum invoice face in native / base column (excludes cancelled), same period as USD helper. */
export function getPeriodTotalBilledBaseFromInvoiceRecords(
	records: InvoiceRecord[],
	filterYear: string,
	filterMonth: string,
	filterFYYear: string,
	filterFYQuarter: string,
): number | null {
	if (!records.length) return null
	const hasFilter =
		!!filterYear ||
		!!filterMonth ||
		!!filterFYYear ||
		!!filterFYQuarter
	if (!hasFilter) return null
	const filtered = filterInvoiceRecordsForTimesheetPeriod(
		records,
		filterYear,
		filterMonth,
		filterFYYear,
		filterFYQuarter,
	)
	let base = 0
	filtered.forEach((inv) => {
		const r = invoiceRowInvoicedPaidOutstanding(inv, 'BASE')
		base += r.invInTotals
	})
	return base
}

export function getPeriodTotalBilledUsdFromInvoiceRecordsSlices(
	records: InvoiceRecord[],
	calYears: string[],
	calMonths: string[],
	fyStarts: string[],
	fyQuarters: string[],
): number | null {
	if (!records.length) return null
	const has =
		calYears.length > 0 ||
		calMonths.length > 0 ||
		fyStarts.length > 0 ||
		fyQuarters.length > 0
	if (!has) return null
	const filtered = filterInvoiceRecordsForTimesheetPeriodSlices(
		records,
		calYears,
		calMonths,
		fyStarts,
		fyQuarters,
	)
	let usd = 0
	filtered.forEach((inv) => {
		usd += parseFloat(String(inv.amount_usd)) || 0
	})
	return usd
}

export function getPeriodTotalBilledBaseFromInvoiceRecordsSlices(
	records: InvoiceRecord[],
	calYears: string[],
	calMonths: string[],
	fyStarts: string[],
	fyQuarters: string[],
): number | null {
	if (!records.length) return null
	const has =
		calYears.length > 0 ||
		calMonths.length > 0 ||
		fyStarts.length > 0 ||
		fyQuarters.length > 0
	if (!has) return null
	const filtered = filterInvoiceRecordsForTimesheetPeriodSlices(
		records,
		calYears,
		calMonths,
		fyStarts,
		fyQuarters,
	)
	let base = 0
	filtered.forEach((inv) => {
		const r = invoiceRowInvoicedPaidOutstanding(inv, 'BASE')
		base += r.invInTotals
	})
	return base
}

export function getPeriodTotalBilledFromInvoiceByPeriod(
	cibp: ClientInvoiceByPeriodJson | null,
	clientId: number | string | undefined,
	filterYear: string,
	filterMonth: string,
	filterFYYear: string,
	filterFYQuarter: string,
): number | null {
	if (!cibp || clientId == null || clientId === '') return null
	const hasFilter =
		!!filterYear ||
		!!filterMonth ||
		!!filterFYYear ||
		!!filterFYQuarter
	if (!hasFilter) return null

	const cid = String(clientId)
	const byMonth = cibp.by_year_month || {}
	const byYear = cibp.by_year || {}

	if (filterYear && filterMonth) {
		const ym = `${filterYear}-${String(filterMonth).padStart(2, '0')}`
		const amount = byMonth[ym]?.clients?.[cid]
		return amount != null ? Number(amount) : null
	}
	if (filterFYYear && filterFYQuarter) {
		const fyStartYear = fyStartYearFromLabel(filterFYYear)
		if (fyStartYear == null) return null
		const quarterUpper = (filterFYQuarter || '').toUpperCase()
		const q =
			quarterUpper === 'Q1' ||
			quarterUpper === 'Q2' ||
			quarterUpper === 'Q3' ||
			quarterUpper === 'Q4'
				? quarterUpper
				: 'Q1'
		const yearMonths = getYearMonthsForFYQuarter(String(fyStartYear), q)
		let sum = 0
		yearMonths.forEach((ym) => {
			const amt = byMonth[ym]?.clients?.[cid]
			if (amt != null) sum += Number(amt)
		})
		return sum
	}
	if (filterFYYear) {
		const fyStartYear = fyStartYearFromLabel(filterFYYear)
		if (fyStartYear == null) return null
		const yearMonths = getYearMonthsForFullFY(String(fyStartYear))
		let sum = 0
		yearMonths.forEach((ym) => {
			const amt = byMonth[ym]?.clients?.[cid]
			if (amt != null) sum += Number(amt)
		})
		return sum
	}
	if (filterYear) {
		const amount = byYear[String(filterYear)]?.clients?.[cid]
		return amount != null ? Number(amount) : null
	}
	return null
}

export function getPeriodTotalBilledFromTrend(
	rows: ClientRevenueTrendRow[],
	filterYear: string,
	filterMonth: string,
	filterFYYear: string,
	filterFYQuarter: string,
): number | null {
	if (!rows?.length) return null
	const hasFilter =
		!!filterYear ||
		!!filterMonth ||
		!!filterFYYear ||
		!!filterFYQuarter
	if (!hasFilter) return null

	const calYear = filterYear ? parseInt(filterYear, 10) : null
	const calMonth = filterMonth ? parseInt(filterMonth, 10) : null

	let sum = 0
	rows.forEach((row) => {
		const revMonth = row.revenue_month
		if (!revMonth || typeof revMonth !== 'string') return
		const parts = revMonth.split('-')
		const year = parseInt(parts[0], 10)
		const month = parseInt(parts[1], 10)
		if (Number.isNaN(year) || Number.isNaN(month)) return
		if (calYear != null && year !== calYear) return
		if (calMonth != null && month !== calMonth) return
		if (filterFYYear || filterFYQuarter) {
			const fy = getFYFromWorkMonth(year, month)
			if (filterFYYear && fy.fyLabel !== filterFYYear) return
			if (filterFYQuarter && fy.quarter !== filterFYQuarter) return
		}
		sum += parseFloat(String(row.monthly_revenue || 0)) || 0
	})
	return sum
}

function ymKeyMatchesPeriodSlices(
	ymKey: string,
	calYears: string[],
	calMonths: string[],
	fyStarts: string[],
	fyQuarters: string[],
): boolean {
	const parts = ymKey.split('-')
	if (parts.length < 2) return false
	const y = parseInt(parts[0], 10)
	const m = parseInt(parts[1], 10)
	if (Number.isNaN(y) || Number.isNaN(m)) return false
	return entryMatchesWorkPeriodFiltersSlices(
		{ year: y, month: m },
		calYears,
		calMonths,
		fyStarts,
		fyQuarters,
	)
}

export function getPeriodTotalBilledFromInvoiceByPeriodSlices(
	cibp: ClientInvoiceByPeriodJson | null,
	clientId: number | string | undefined,
	calYears: string[],
	calMonths: string[],
	fyStarts: string[],
	fyQuarters: string[],
): number | null {
	if (!cibp || clientId == null || clientId === '') return null
	const has =
		calYears.length > 0 ||
		calMonths.length > 0 ||
		fyStarts.length > 0 ||
		fyQuarters.length > 0
	if (!has) return null
	const byMonth = cibp.by_year_month || {}
	const cid = String(clientId)
	let sum = 0
	for (const ym of Object.keys(byMonth)) {
		if (!ymKeyMatchesPeriodSlices(ym, calYears, calMonths, fyStarts, fyQuarters)) {
			continue
		}
		const amt = byMonth[ym]?.clients?.[cid]
		if (amt != null) sum += Number(amt)
	}
	return sum
}

export function getPeriodTotalBilledFromTrendSlices(
	rows: ClientRevenueTrendRow[],
	calYears: string[],
	calMonths: string[],
	fyStarts: string[],
	fyQuarters: string[],
): number | null {
	if (!rows?.length) return null
	const has =
		calYears.length > 0 ||
		calMonths.length > 0 ||
		fyStarts.length > 0 ||
		fyQuarters.length > 0
	if (!has) return null
	let sum = 0
	rows.forEach((row) => {
		const revMonth = row.revenue_month
		if (!revMonth || typeof revMonth !== 'string') return
		const parts = revMonth.split('-')
		const year = parseInt(parts[0], 10)
		const month = parseInt(parts[1], 10)
		if (Number.isNaN(year) || Number.isNaN(month)) return
		if (
			!entryMatchesWorkPeriodFiltersSlices(
				{ year, month },
				calYears,
				calMonths,
				fyStarts,
				fyQuarters,
			)
		) {
			return
		}
		sum += parseFloat(String(row.monthly_revenue || 0)) || 0
	})
	return sum
}

/* ─── Effective rate from line items (cap $100/hr) ─────────────────────── */

export function getFilteredEffectiveRate(
	data: { detailed_invoices?: { invoice_records?: InvoiceRecord[] } },
	fallbackBilled: number,
	fallbackBillable: number,
): number | null {
	const RATE_CAP = 100
	const invoiceRecords = data?.detailed_invoices?.invoice_records || []
	let filteredSum = 0
	let filteredQty = 0
	for (const inv of invoiceRecords) {
		for (const item of inv.line_items || []) {
			const qty = parseFloat(String(item.quantity)) || 0
			const lineTotal =
				parseFloat(String(item.line_total_usd ?? item.line_total)) || 0
			if (qty <= 0) continue
			const rate = lineTotal / qty
			if (rate > 0 && rate <= RATE_CAP) {
				filteredSum += lineTotal
				filteredQty += qty
			}
		}
	}
	if (filteredQty > 0) return filteredSum / filteredQty
	if (fallbackBillable > 0 && fallbackBilled > 0) return fallbackBilled / fallbackBillable
	return null
}

/* ─── Filter option lists from cached gold-fact ─────────────────────────── */

export function buildTimesheetFilterOptionsFromCache(
	cachedProjectData: { projectId: number; data: GoldFactProjectUserMonthRow[] }[],
): {
	calYears: number[]
	calMonths: number[]
	fyYears: string[]
	fyQuarters: { value: string; label: string; months: string }[]
} {
	if (!cachedProjectData?.length) {
		return { calYears: [], calMonths: [], fyYears: [], fyQuarters: [] }
	}
	const years = new Set<number>()
	const months = new Set<number>()
	const fyLabels = new Set<string>()
	cachedProjectData.forEach(({ data }) => {
		if (!Array.isArray(data)) return
		data.forEach((entry) => {
			if (entry.year != null) years.add(Number(entry.year))
			if (entry.month != null) months.add(Number(entry.month))
			const fy = getFYFromWorkMonth(entry.year, entry.month)
			if (fy.fyLabel) fyLabels.add(fy.fyLabel)
		})
	})
	const maxFYStart = getMaxIndianFYStartYear()
	const calYears = [...years]
		.filter((y) => Number(y) <= new Date().getFullYear())
		.sort((a, b) => b - a)
	const calMonths = [...months].sort((a, b) => a - b)
	const fyYears = [...fyLabels]
		.filter((label) => {
			const startYear = parseInt(
				String(label).replace(/^FY\s*/i, '').trim().split('-')[0],
				10,
			)
			return !Number.isNaN(startYear) && startYear <= maxFYStart
		})
		.sort((a, b) => b.localeCompare(a))
	const fyQuarters = [
		{ value: 'q1', label: 'Q1', months: 'Apr–Jun' },
		{ value: 'q2', label: 'Q2', months: 'Jul–Sep' },
		{ value: 'q3', label: 'Q3', months: 'Oct–Dec' },
		{ value: 'q4', label: 'Q4', months: 'Jan–Mar' },
	]
	return { calYears, calMonths, fyYears, fyQuarters }
}

export function availableFYYearsFromCachedData(
	cachedProjectData: { projectId: number; data: GoldFactProjectUserMonthRow[] }[],
): string[] {
	const { fyYears } = buildTimesheetFilterOptionsFromCache(cachedProjectData)
	return fyYears
}

/* ─── Per-project stats & sort ──────────────────────────────────────────── */

export interface ProjectDetailStats {
	totalHours: number
	billableHours: number
	nonBillableHours: number
	totalUsers: number
	totalEntries: number
	dateRange: { start: string | null; end: string | null }
	totalMonths: number
}

export function calculateProjectStatsFromEntries(
	entries: GoldFactProjectUserMonthRow[],
): ProjectDetailStats | null {
	if (!entries?.length) return null
	const months = new Set<string>()
	const users = new Set<number>()
	let totalHours = 0
	let billableHours = 0
	let nonBillableHours = 0
	let totalEntries = 0
	let dateStart: string | null = null
	let dateEnd: string | null = null

	entries.forEach((entry) => {
		totalHours += parseFloat(String(entry.total_hours || 0)) || 0
		billableHours += parseFloat(String(entry.billable_hours || 0)) || 0
		nonBillableHours += parseFloat(String(entry.non_billable_hours || 0)) || 0
		if (entry.user_id != null) users.add(Number(entry.user_id))
		totalEntries += parseInt(String(entry.entries_count || 0), 10) || 0
		const y = entry.year
		const mo = entry.month
		if (y != null && mo != null) {
			const dateKey = `${y}-${String(mo).padStart(2, '0')}`
			months.add(dateKey)
			if (!dateStart || dateKey < dateStart) dateStart = dateKey
			if (!dateEnd || dateKey > dateEnd) dateEnd = dateKey
		}
	})

	return {
		totalHours,
		billableHours,
		nonBillableHours,
		totalUsers: users.size,
		totalEntries,
		dateRange: { start: dateStart, end: dateEnd },
		totalMonths: months.size,
	}
}

export function getUniqueUsersFromGoldRows(
	rows: GoldFactProjectUserMonthRow[],
): { id: number; name: string; username: string }[] {
	const map = new Map<number, { id: number; name: string; username: string }>()
	rows.forEach((e) => {
		if (e.user_id == null) return
		const id = Number(e.user_id)
		const name = String(e.full_name || e.username || 'Unknown')
		const username = String(e.username || '')
		if (!map.has(id)) map.set(id, { id, name, username })
	})
	return [...map.values()].sort((a, b) => a.name.localeCompare(b.name))
}

export type GoldFactSortColumn =
	| 'year-month'
	| 'user'
	| 'total-hours'
	| 'billable'
	| 'non-billable'
	| 'entries'

export function sortGoldFactRows(
	rows: GoldFactProjectUserMonthRow[],
	sortColumn: GoldFactSortColumn,
	sortDirection: 'asc' | 'desc',
): GoldFactProjectUserMonthRow[] {
	const sorted = [...rows].sort((a, b) => {
		let cmp = 0
		switch (sortColumn) {
			case 'year-month': {
				const aYm = `${a.year ?? 0}-${String(a.month ?? 0).padStart(2, '0')}`
				const bYm = `${b.year ?? 0}-${String(b.month ?? 0).padStart(2, '0')}`
				cmp = aYm.localeCompare(bYm)
				break
			}
			case 'user': {
				const aUser = String(a.full_name || a.username || '').toLowerCase()
				const bUser = String(b.full_name || b.username || '').toLowerCase()
				cmp = aUser.localeCompare(bUser)
				break
			}
			case 'total-hours':
				cmp =
					(parseFloat(String(a.total_hours || 0)) || 0) -
					(parseFloat(String(b.total_hours || 0)) || 0)
				break
			case 'billable':
				cmp =
					(parseFloat(String(a.billable_hours || 0)) || 0) -
					(parseFloat(String(b.billable_hours || 0)) || 0)
				break
			case 'non-billable':
				cmp =
					(parseFloat(String(a.non_billable_hours || 0)) || 0) -
					(parseFloat(String(b.non_billable_hours || 0)) || 0)
				break
			case 'entries':
				cmp =
					(parseInt(String(a.entries_count || 0), 10) || 0) -
					(parseInt(String(b.entries_count || 0), 10) || 0)
				break
			default:
				break
		}
		return sortDirection === 'asc' ? cmp : -cmp
	})
	return sorted
}
